import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { db } from '@/services/storage/db';
import { SeasonService } from '@/services/season-service';
import { TaskService } from '@/services/task-service';
import { LogEntryService } from '@/services/log-entry-service';
import { DashboardService } from '@/services/dashboard-service';
import { useTaskStore } from '@/stores/task-store';
import { useLogEntryStore } from '@/stores/log-entry-store';
import { usePropertyStore } from '@/stores/property-store';
import { useUserProfileStore } from '@/stores/user-profile-store';
import { useSeasonStore } from '@/stores/season-store';
import { getCountdownSeason } from '@/services/dashboard-service';
import type { DashboardData, SeasonTaskStats } from '@/services/dashboard-service';
import type { SeasonHuntStats } from '@/utils/countdown-utils';

import { CountdownWidget } from './CountdownWidget';
import { SeasonPills } from './season-pills';
import { ActiveHuntCard } from './ActiveHuntCard';
import { HarvestHighlightCard } from './HarvestHighlightCard';
import { OffSeasonPrepWidget } from './OffSeasonPrepWidget';
import { SeasonRecapCard } from './SeasonRecapCard';
import {
  CollapsibleConditions,
  CollapsibleRecentScouts,
} from './widget-wrappers';
import { PrepCenter } from './PrepCenter';
import { SetupMissionCard } from './SetupMissionCard';
import { WeatherAlertsSection } from '@/components/weather/WeatherAlertsSection';

type PageState =
  | { status: 'loading' }
  | { status: 'ready'; data: DashboardData }
  | { status: 'error'; message: string };

function getTaskStatsForSeason(
  seasonId: string,
  taskStats: SeasonTaskStats[],
): SeasonTaskStats {
  return (
    taskStats.find((s) => s.seasonId === seasonId) ?? {
      seasonId,
      seasonName: '',
      total: 0,
      completed: 0,
      overdue: 0,
      blocked: 0,
    }
  );
}

async function fetchSeasonHuntStats(seasonId: string): Promise<SeasonHuntStats> {
  const [hunts, harvests] = await Promise.all([
    db.getHuntsBySeason(seasonId),
    db.getHarvestRecordsBySeason(seasonId),
  ]);

  let totalSits = 0;
  let morningSits = 0;
  let eveningSits = 0;
  const sitDates = new Set<string>();

  for (const hunt of hunts) {
    const sits = await db.getHuntSitsByHunt(hunt.id);
    totalSits += sits.length;
    morningSits += sits.filter((s) => s.period === 'morning').length;
    eveningSits += sits.filter((s) => s.period === 'evening').length;
    sits.forEach((s) => sitDates.add(s.date));
  }

  return {
    totalSits,
    morningSits,
    eveningSits,
    totalHarvests: harvests.length,
    daysAfield: sitDates.size,
  };
}

function LoadingSkeleton() {
  return (
    <div className="flex flex-col gap-4 animate-pulse" aria-label="Loading dashboard">
      <div className="h-36 bg-[var(--color-elevated)] rounded-lg" />
      <div className="h-16 bg-[var(--color-elevated)] rounded-lg" />
      <div className="h-48 bg-[var(--color-elevated)] rounded-lg" />
      <div className="h-32 bg-[var(--color-elevated)] rounded-lg" />
    </div>
  );
}


export function DashboardPage() {
  const dashboardService = useMemo(
    () => new DashboardService(new SeasonService(db), new TaskService(db), new LogEntryService(db)),
    [],
  );
  const [pageState, setPageState] = useState<PageState>({ status: 'loading' });
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedSeasonId, setSelectedSeasonId] = useState<string | null>(null);
  const [seasonHuntStats, setSeasonHuntStats] = useState<SeasonHuntStats | null>(null);
  const [taskErrors, setTaskErrors] = useState<Record<string, string>>({});
  const navigate = useNavigate();
  const reactLocation = useLocation();
  const { completeTask, optimisticallyRemoveTask, reloadUpcomingTasksSilent, loadTasksDueThisMonth } = useTaskStore();
  // Subscribe to store.tasks directly so optimistic removals cause immediate re-renders
  const storeTasks = useTaskStore((state) => state.tasks);
  // Subscribe to the log-entry and task store revision counters so out-of-band mutations
  // (a log entry created elsewhere, a task added on another page) invalidate the
  // getDashboardData() snapshot — see the edge-triggered effect below
  // (defect: dashboardpage-stale-snapshot-body-consumers.md).
  const logEntryRevision = useLogEntryStore((s) => s.revision);
  const taskRevision = useTaskStore((s) => s.revision);
  const { properties, loadProperties } = usePropertyStore();
  const { preferences, loadProfile } = useUserProfileStore();

  // ── Onboarding toast ────────────────────────────────────────────────────────
  const [toastMessage, setToastMessage] = useState<string | null>(() => {
    const state = reactLocation.state as { toastMessage?: string } | null;
    return state?.toastMessage ?? null;
  });
  useEffect(() => {
    if (!toastMessage) return;
    const timer = setTimeout(() => setToastMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [toastMessage]);
  const {
    seasons: storeSeasons,
    isLoading: seasonsLoading,
    seasonsLoaded,
    loadSeasons,
    primarySeason,
  } = useSeasonStore();

  // ── Data loading effects ────────────────────────────────────────────────
  useEffect(() => {
    if (properties.length === 0) void loadProperties();
  }, [properties.length, loadProperties]);

  useEffect(() => {
    if (!preferences) void loadProfile();
  }, [preferences, loadProfile]);

  useEffect(() => {
    if (storeSeasons.length === 0) void loadSeasons();
  }, [storeSeasons.length, loadSeasons]);

  // The dashboard has two async paths for seasons: getDashboardData() and loadSeasons().
  // If loadSeasons() resolves first (storeSeasons populated before pageState becomes 'ready'),
  // the old prev===0 guard never fires because storeSeasons.length is already N when the
  // 'ready' transition happens. This one-shot retry closes that race: when pageState is 'ready'
  // but getDashboardData() returned empty seasons while storeSeasons has data, re-fetch once.
  const hasRetriedEmptySeasonsRef = useRef(false);
  // Derive season count before early returns so it can be used in this unconditional effect.
  const dashboardSeasonsCount = pageState.status === 'ready' ? pageState.data.seasons.length : -1;
  useEffect(() => {
    if (
      !hasRetriedEmptySeasonsRef.current &&
      pageState.status === 'ready' &&
      dashboardSeasonsCount === 0 &&
      storeSeasons.length > 0
    ) {
      hasRetriedEmptySeasonsRef.current = true;
      setRefreshKey((k) => k + 1);
    }
  }, [pageState.status, dashboardSeasonsCount, storeSeasons.length]);

  // ── O-1: invalidate the getDashboardData() snapshot on a primary-season flip ──
  // Defect O-1 (docs/defects/dashboardpage-stale-primary-season.md): store.setPrimary
  // (season-store.ts:116-128) mutates only the live Zustand array, never pageData.seasons
  // (the getDashboardData snapshot at DashboardPage.tsx:219), so the snapshot retains stale
  // isPrimary flags and getCountdownSeason (dashboard-service.ts:34-40) selects the old
  // primary until the next mount. We bump refreshKey to re-run the getDashboardData effect
  // (:192-215) and rebuild the snapshot's isPrimary flags — the same refreshKey-invalidation
  // pattern used by the error "Try again" handler (:404), ActiveHuntCard onHuntEnded (:519),
  // and the empty-seasons retry guard above (:157).
  //
  // CRITICAL: this must be EDGE-triggered on the primary id transitioning, not LEVEL-triggered
  // on primarySeason() being truthy — a level trigger would bump refreshKey on every render and
  // loop against the getDashboardData effect. The ref holds the last-seen primary id; we bump
  // only on a genuine transition, mirroring the one-shot-guard shape of the retry guard above.
  const lastPrimarySeasonIdRef = useRef<string | null | undefined>(undefined);
  // Read the live primary id here (not via the `primary` const, which is declared below) so this
  // effect can sit alongside the other unconditional effects above the early returns.
  const primarySeasonId = primarySeason()?.id ?? null;
  useEffect(() => {
    // undefined sentinel = ref not yet seeded (first observation). Seed without bumping so the
    // initial getDashboardData snapshot is trusted; bump only when the id actually changes after.
    if (lastPrimarySeasonIdRef.current === undefined) {
      lastPrimarySeasonIdRef.current = primarySeasonId;
      return;
    }
    if (lastPrimarySeasonIdRef.current !== primarySeasonId) {
      lastPrimarySeasonIdRef.current = primarySeasonId;
      setRefreshKey((k) => k + 1);
    }
  }, [primarySeasonId]);

  // ── Snapshot-body invalidation on out-of-band log-entry / task mutations ──
  // Defect dashboardpage-stale-snapshot-body-consumers.md: recentLogEntries and taskStats are
  // read from the getDashboardData() snapshot (pageData, captured at the getDashboardData effect
  // below). The O-1 invalidation (effect at :179-190) only fires on a primary-season-id flip, so
  // a log entry created/edited elsewhere or an out-of-band task mutation (e.g. an addTask that
  // changes a season's total — the residual gap liveTaskStats at :318-349 cannot cover, since it
  // only adjusts `completed`, never `total`) leaves those surfaces stale until the next mount.
  // The log-entry-store (log-entry-store.ts:revision) and task-store (task-store.ts:revision)
  // each bump a monotonic revision on every mutation; we bump refreshKey on a revision transition
  // to re-run the getDashboardData effect and rebuild the snapshot — the same refreshKey-
  // invalidation pattern used by the O-1 primary-flip effect (:179-190), the error "Try again"
  // handler (:404), ActiveHuntCard onHuntEnded (:519), and the empty-seasons retry guard (:157).
  //
  // EDGE-triggered, mirroring O-1: the undefined sentinel seeds the ref without bumping on first
  // observation (the initial getDashboardData snapshot is trusted), so we bump only when a
  // revision actually changes after mount. Refs mirror lastPrimarySeasonIdRef (:175).
  const lastLogRevisionRef = useRef<number | undefined>(undefined);
  const lastTaskRevisionRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    // Seed both refs on first observation without bumping; trust the mount snapshot.
    if (lastLogRevisionRef.current === undefined || lastTaskRevisionRef.current === undefined) {
      lastLogRevisionRef.current = logEntryRevision;
      lastTaskRevisionRef.current = taskRevision;
      return;
    }
    if (
      lastLogRevisionRef.current !== logEntryRevision ||
      lastTaskRevisionRef.current !== taskRevision
    ) {
      lastLogRevisionRef.current = logEntryRevision;
      lastTaskRevisionRef.current = taskRevision;
      setRefreshKey((k) => k + 1);
    }
  }, [logEntryRevision, taskRevision]);

  useEffect(() => {
    let active = true;
    dashboardService
      .getDashboardData()
      .then((data) => {
        if (active) {
          // Seed the task store so optimistic removals can trigger immediate re-renders
          useTaskStore.setState({ tasks: data.upcomingTasks });
          setPageState({ status: 'ready', data });
          // Load month-scoped tasks for off-season prep widget (M64)
          void loadTasksDueThisMonth();
        }
      })
      .catch((err) => {
        if (active) {
          const message = err instanceof Error ? err.message : 'Failed to load dashboard';
          console.error('DashboardPage.load:', err);
          setPageState({ status: 'error', message });
        }
      });
    return () => {
      active = false;
    };
  }, [dashboardService, refreshKey, loadTasksDueThisMonth]);

  // ── Derive active season early (before early returns) so the hunt-stats
  //    effect can be placed unconditionally above the conditional renders. ──
  const pageData = pageState.status === 'ready' ? pageState.data : null;
  const primary = primarySeason();
  const orderedSeasons = useMemo(() => {
    if (!pageData) return [];
    return primary
      ? [primary, ...pageData.seasons.filter((s) => s.id !== primary.id)]
      : pageData.seasons;
  }, [pageData, primary]);

  const activeSeason = useMemo(() => {
    if (orderedSeasons.length === 0) return null;
    if (selectedSeasonId) {
      return orderedSeasons.find((s) => s.id === selectedSeasonId) ?? orderedSeasons[0];
    }
    return orderedSeasons[0];
  }, [orderedSeasons, selectedSeasonId]);

  const activeSeasonId = activeSeason?.id ?? null;

  // ── M64: Off-season derivations ─────────────────────────────────────────────
  // isOffSeason: true when no season's date range encompasses today.
  // (activeSeason being non-null only means "there are seasons"; it doesn't mean one is current.)
  const isOffSeason = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return orderedSeasons.every(
      (s) => s.openerDate > today || (s.closerDate != null && s.closerDate < today),
    );
  }, [orderedSeasons]);

  // mostRecentEndedSeason: latest season whose closerDate is in the past
  const mostRecentEndedSeason = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return (
      orderedSeasons
        .filter((s) => s.closerDate != null && s.closerDate < today)
        .sort((a, b) => (b.closerDate ?? '').localeCompare(a.closerDate ?? ''))[0] ?? null
    );
  }, [orderedSeasons]);

  // Load hunt sit/harvest stats whenever the active season changes.
  // Must be placed before any conditional returns to satisfy Rules of Hooks.
  useEffect(() => {
    if (!activeSeasonId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- early-return branch clears state before async path
      setSeasonHuntStats(null);
      return;
    }
    let active = true;
    fetchSeasonHuntStats(activeSeasonId)
      .then((stats) => {
        if (active) setSeasonHuntStats(stats);
      })
      .catch((err) => {
        console.error('DashboardPage.fetchSeasonHuntStats:', err);
      });
    return () => {
      active = false;
    };
  }, [activeSeasonId]);

  const handleSelectSeason = useCallback((seasonId: string) => {
    setSelectedSeasonId(seasonId);
  }, []);

  // ── Season shown in CountdownWidget — shared by liveTaskStats and PrepCenter ──
  // J-07: use getCountdownSeason (primary wins → earliest-opener fallback → null).
  // orderedSeasons is derived from pageData.seasons, which is rebuilt whenever refreshKey
  // changes. O-1 fix (effect at :179-190): a primary-season flip via store.setPrimary now
  // bumps refreshKey on the primary-id transition, so the getDashboardData effect (:192-215)
  // re-runs and the snapshot's isPrimary flags are rebuilt — making this selector reactive to
  // setPrimary (previously the snapshot held stale isPrimary flags until the next mount; see
  // docs/defects/dashboardpage-stale-primary-season.md).
  const countdownSeason = useMemo(() => getCountdownSeason(orderedSeasons), [orderedSeasons]);
  const countdownSeasonId = countdownSeason?.id ?? null;

  // ── I-8: Reactive liveTaskStats for CountdownWidget ──────────────────────────
  // We derive a live completed count using a hybrid of two signals:
  //
  //   completedInStore: tasks in storeTasks marked completed === true for this season.
  //     Covers the completeTask path where the store marks a task completed in place.
  //
  //   absentFromBase: tasks from pageState.data.upcomingTasks (the load-time snapshot,
  //     which only contains non-completed upcoming tasks) that are no longer present in
  //     storeTasks for this season. Covers optimistic removals (task removed from the
  //     store before completeTask resolves) and post-reload confirmed completions.
  //
  // Why NOT a simple delta (baseUpcomingCount - currentUpcomingCount):
  //   getDashboardData caps the upcoming fetch at 20 tasks. Before the fix,
  //   reloadUpcomingTasksSilent used a default limit of 10, so tasks at positions 11–20
  //   of the base snapshot would be absent from the reload and misidentified as completed.
  //   Both fetches now use the same 20-task window so absentFromBase reflects only
  //   tasks that genuinely completed (or were optimistically removed), not window mismatches.
  //
  // This formula handles all cases:
  //   • optimisticallyRemoveTask: task leaves storeTasks → absentFromBase +1
  //   • completeTask (in-place mark): task stays in storeTasks with completed=true → completedInStore +1
  //   • reloadUpcomingTasksSilent: confirmed-completed tasks leave storeTasks → absentFromBase +1
  //     (tasks completed in prior sessions were never in baseUpcoming, so they don't inflate the count)
  //   • rollback: storeTasks restored → absent count drops back to 0
  const liveTaskStats = useMemo((): import('@/services/dashboard-service').SeasonTaskStats | null => {
    if (pageState.status !== 'ready') return null;
    const { taskStats: baseStats, upcomingTasks: baseUpcoming } = pageState.data;

    if (!countdownSeason) return null;

    const seasonId = countdownSeason.id;
    const baseSnapshot = getTaskStatsForSeason(seasonId, baseStats);

    // Tasks from the load-time upcoming snapshot that are now absent from the store
    // (either optimistically removed or confirmed-completed and excluded from re-fetch)
    const storeTaskIds = new Set(storeTasks.map((t) => t.id));
    const absentFromBase = baseUpcoming.filter(
      (t) => t.seasonId === seasonId && !storeTaskIds.has(t.id),
    ).length;

    // Tasks currently in the store that are marked completed (in-place completeTask path)
    const completedInStore = storeTasks.filter(
      (t) => t.seasonId === seasonId && t.completed,
    ).length;

    const liveCompleted = Math.max(
      0,
      Math.min(baseSnapshot.total, baseSnapshot.completed + absentFromBase + completedInStore),
    );

    return {
      ...baseSnapshot,
      completed: liveCompleted,
      // total and overdue remain from snapshot — quick-complete doesn't change them
    };
  }, [pageState, storeTasks, countdownSeason]);

  async function handleQuickComplete(id: string): Promise<void> {
    // Snapshot current tasks for rollback on failure
    const snapshot = useTaskStore.getState().tasks;
    // Optimistically remove immediately — no isLoading flip
    optimisticallyRemoveTask(id);
    try {
      await completeTask(id);
      // Reconcile upcoming tasks silently after success
      await reloadUpcomingTasksSilent(20);
    } catch (err) {
      // Restore snapshot on failure
      useTaskStore.setState({ tasks: snapshot });
      const message = err instanceof Error ? err.message : 'Failed to complete task';
      setTaskErrors((prev) => ({ ...prev, [id]: message }));
      // Auto-clear error after 3 seconds
      setTimeout(() => {
        setTaskErrors((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }, 3000);
      // Error is fully handled: snapshot restored, error displayed, auto-clear scheduled.
      // Do not re-throw — caller is a UI event handler with no consumer for the rejection.
    }
  }

  function handleEntryClick(id: string) {
    const entry = pageData?.recentLogEntries.find((e) => e.id === id);
    if (entry) {
      navigate(`/waypoints/${entry.waypointId}/log/${entry.id}`);
    }
  }

  function handleAddLog() {
    navigate('/scout/log/new');
  }

  // ── Early returns (all hooks are above this line) ─────────────────────────
  if (pageState.status === 'loading') {
    return <LoadingSkeleton />;
  }

  if (pageState.status === 'error') {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="font-display text-display-lg text-[var(--color-text-display)]">Dashboard</h1>
        <div className="bg-[var(--color-surface)] border border-[var(--color-accent-subtle)] rounded-lg p-4">
          <p className="text-body-sm text-[var(--color-accent-hover)]">{pageState.message}</p>
          <button
            type="button"
            onClick={() => {
              setPageState({ status: 'loading' });
              setRefreshKey((k) => k + 1);
            }}
            className="mt-3 text-body-sm text-[var(--color-accent)] hover:text-[var(--color-accent-hover)] underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // pageState.status === 'ready' guaranteed past this point
  const { seasons, taskStats, recentLogEntries } = pageState.data;

  // Derive display tasks from the store so optimistic removals cause immediate UI updates.
  // storeTasks is seeded from getDashboardData() and mutated by optimisticallyRemoveTask.
  const upcomingTasks = storeTasks;

  // Filter upcoming tasks to the countdown season so PrepCenter and CountdownWidget
  // always show the same task count. This avoids desync when tasks from multiple
  // seasons are present in storeTasks (Fix 5 + Fix 6).
  const prepCenterTasks = countdownSeasonId
    ? storeTasks.filter((t) => t.seasonId === countdownSeasonId)
    : storeTasks;
  const hasSeasons = seasons.length > 0;
  const hasProperties = properties.length > 0;

  if (!hasSeasons) {
    // Keep showing the skeleton while seasons are still loading from the store,
    // or while a retry is in flight because storeSeasons has data that getDashboardData
    // didn't return yet (race condition: loadSeasons completed before getDashboardData).
    if (!seasonsLoaded || seasonsLoading || storeSeasons.length > 0) {
      return <LoadingSkeleton />;
    }

    // Scenario A — no season, no property, no waypoints: show SetupMissionCard as the primary CTA
    // Scenario C — no season, has property: show SetupMissionCard + ConditionsWidget
    return (
      <div
        className="flex flex-col gap-6"
        data-testid="dashboard-page"
      >
        {toastMessage && (
          <div
            className="bg-[var(--color-surface)] border border-[var(--color-accent-subtle)] rounded-lg p-3 text-body-sm text-[var(--color-text-primary)]"
            data-testid="dashboard-status-toast"
            role="status"
            aria-live="polite"
          >
            {toastMessage}
          </div>
        )}
        <SetupMissionCard />
        {hasProperties && (
          <CollapsibleConditions
            properties={properties}
            primaryPropertyId={properties.find((p) => p.isPrimary)?.id ?? null}
          />
        )}
      </div>
    );
  }

  // Scenario C — has season but no property yet: show SetupMissionCard above main content
  // Scenario D — fully onboarded: SetupMissionCard returns null automatically
  // In both C and D, the main dashboard layout renders below

  return (
    <div className="flex flex-col gap-3" data-testid="dashboard-page">
      <h1 className="font-display text-display-lg text-[var(--color-text-display)]">Dashboard</h1>

      {/* Onboarding toast — shown after completing a setup step */}
      {toastMessage && (
        <div
          className="bg-[var(--color-surface)] border border-[var(--color-accent-subtle)] rounded-lg p-3 text-body-sm text-[var(--color-text-primary)]"
          data-testid="dashboard-status-toast"
          role="status"
          aria-live="polite"
        >
          {toastMessage}
        </div>
      )}

      {/* Setup mission card — three-state: incomplete list / completion state / dismissed (null).
          Card manages its own visibility so it shows the completion state when all steps are done. */}
      <SetupMissionCard />

      {/* 1. Season Countdown Hero — always visible, never collapsible.
          J-07: countdownSeason is now driven by getCountdownSeason (primary wins → earliest-opener
          fallback → null). huntStats only applies when the countdown season is the active season
          (i.e. activeSeason matches countdownSeason).
          I-8: liveTaskStats derived from reactive storeTasks so completed count updates
          immediately when optimisticallyRemoveTask fires — no page refresh needed. */}
      {countdownSeason && (
        <CountdownWidget
          season={countdownSeason}
          taskStats={liveTaskStats ?? getTaskStatsForSeason(countdownSeason.id, taskStats)}
          variant="hero"
          huntStats={
            activeSeason?.id === countdownSeason.id ? (seasonHuntStats ?? undefined) : undefined
          }
        />
      )}
      {activeSeason && orderedSeasons.length > 1 && (
        <SeasonPills
          seasons={orderedSeasons}
          primarySeasonId={activeSeason.id}
          onSelectSeason={handleSelectSeason}
        />
      )}

      {/* 2. Active Hunt Card — shows only when a hunt is in progress */}
      {activeSeason && (
        <ActiveHuntCard
          seasonId={activeSeason.id}
          onHuntEnded={() => setRefreshKey((k) => k + 1)}
        />
      )}

      {/* 3. Harvest Highlight — M62 */}
      {activeSeason && <HarvestHighlightCard seasonId={activeSeason.id} />}

      {/* 3b. Off-Season Prep Widget — M64: shows between countdown and next-action only off-season */}
      {isOffSeason && (
        <OffSeasonPrepWidget
          onQuickComplete={handleQuickComplete}
          taskErrors={taskErrors}
        />
      )}

      {/* 3c. Season Recap Card — M64: shows whenever there is a recently ended season */}
      {mostRecentEndedSeason && (
        <SeasonRecapCard season={mostRecentEndedSeason} />
      )}

      {/* 4. PrepCenter — consolidates Next Action + Upcoming Tasks.
          Uses prepCenterTasks (season-filtered) so the count matches CountdownWidget. */}
      <PrepCenter
        tasks={prepCenterTasks}
        seasonId={countdownSeasonId ?? (primary?.id ?? upcomingTasks[0]?.seasonId)}
        onQuickComplete={handleQuickComplete}
        taskErrors={taskErrors}
      />

      {/* 5. Today's Conditions */}
      <CollapsibleConditions
        properties={properties}
        primaryPropertyId={properties.find((p) => p.isPrimary)?.id ?? null}
      />

      {/* 6. Weather Alerts */}
      <WeatherAlertsSection />

      {/* 8. Recent Scout Entries */}
      <CollapsibleRecentScouts
        entries={recentLogEntries}
        onEntryClick={handleEntryClick}
        onAddClick={handleAddLog}
      />

      {/* 9. Team Activity — M-Team scope */}
      {/* TODO M-Team: <CollapsibleTeamActivity /> — renders when team tier is active */}

      {/* 10. AI Insights — M-AI-Insights scope */}
      {/* TODO M-AI-Insights: <CollapsibleAIInsights /> — renders when AI features are enabled */}
    </div>
  );
}
